import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DetailModal } from '../../ui/Modal';
import InventoryDetailModal from '../../inventory/InventoryDetailModal';
import { soldItemsService, type SoldPieceDetail } from '../../../services/soldItems.service';
import { formatCurrency } from '../../../utils/formatters/money';
import { formatCommercialSaleDate } from '../../../utils/sales/commercial-sale-date';
import { buildInventoryDetailSaleContext } from '../../../utils/inventory/inventoryDetailSaleContext';
import { mapWarehouseInventoryProductApiToInventoryItem } from '../../../utils/inventory/mapWarehouseInventoryProductToInventoryItem';
import { ProductHistorySection } from '../orders/OrderDetailsModal/ProductHistorySection';
import { mapSoldPieceHistoryToProductHistory } from '../orders/OrderDetailsModal/productOrderHistory.utils';
import { PAYMENT_METHOD_LABELS, SALES_ORDER_STATUS_CONFIG } from '../../../utils/constants';
import { getSaleInvoiceLabel } from '../../../utils/sales/sale-invoice.utils';
import { BRANCH_RECEIPT_NUMBERING_ENABLED } from '../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../utils/sales/branch-receipt-numbering';
import * as S from '../orders/OrderDetailsModal/OrderDetailsModal.styles';
import { getDisplayablePaymentNotes } from '../../../utils/paymentDisplayNotes.utils';

type DetailTab = 'piece' | 'sale';

interface SoldPieceDetailModalProps {
  open: boolean;
  salesOrderItemId: number | null;
  onClose: () => void;
}

const formatDate = (date: Date | string) =>
  new Date(date).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatDiscountDisplay = (
  discountType: string,
  discountValue: number,
  originalPrice: number,
): string => {
  if (discountType === 'PERCENTAGE') {
    const equivalentAmount = originalPrice * (discountValue / 100);
    return `${discountValue}% (${formatCurrency(equivalentAmount)})`;
  }
  return formatCurrency(discountValue);
};

const SoldPieceDetailModal: React.FC<SoldPieceDetailModalProps> = ({
  open,
  salesOrderItemId,
  onClose,
}) => {
  const [detail, setDetail] = useState<SoldPieceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('piece');

  const loadDetail = useCallback(async () => {
    if (salesOrderItemId == null) return;
    setLoading(true);
    setError(null);
    try {
      const data = await soldItemsService.getDetail(salesOrderItemId);
      setDetail(data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'No se pudo cargar el detalle.';
      setError(msg);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [salesOrderItemId]);

  useEffect(() => {
    if (open && salesOrderItemId != null) {
      void loadDetail();
    } else {
      setDetail(null);
      setError(null);
      setActiveTab('piece');
    }
  }, [open, salesOrderItemId, loadDetail]);

  const pieceItem = useMemo(() => {
    if (!detail?.piece) return null;
    try {
      return mapWarehouseInventoryProductApiToInventoryItem(
        detail.piece as Parameters<typeof mapWarehouseInventoryProductApiToInventoryItem>[0],
      );
    } catch {
      return null;
    }
  }, [detail?.piece]);

  const embeddedHistory = useMemo(() => {
    if (!detail?.pieceHistory?.length) return [];
    return mapSoldPieceHistoryToProductHistory(detail.pieceHistory);
  }, [detail?.pieceHistory]);

  const hasDiscount =
    detail?.saleLine.discountType &&
    detail.saleLine.discountType !== 'NONE' &&
    (detail.saleLine.discountValue ?? 0) > 0;

  const statusLabel =
    detail?.order.status &&
    (SALES_ORDER_STATUS_CONFIG[detail.order.status as keyof typeof SALES_ORDER_STATUS_CONFIG]
      ?.label ??
      detail.order.status);

  return (
    <DetailModal
      open={open}
      onClose={onClose}
      title={
        detail ? `Pieza vendida: ${detail.summary.productCodeUnique}` : 'Detalle de pieza vendida'
      }
      maxWidth={false}
      paperSx={{ width: 'min(92vw, 1100px)', maxWidth: 'min(92vw, 1100px)' }}
      footerActions={<S.CloseButton onClick={onClose}>Cerrar</S.CloseButton>}
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {error && !loading && <Alert severity="error">{error}</Alert>}
      {detail && !loading && (
        <Box>
          <Tabs
            value={activeTab}
            onChange={(_event, value: DetailTab) => setActiveTab(value)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Pieza" value="piece" />
            <Tab label="Venta" value="sale" />
          </Tabs>

          {activeTab === 'piece' && (
            <Box sx={{ pt: 1 }}>
              {pieceItem ? (
                <InventoryDetailModal
                  embedded
                  readOnly
                  open={open}
                  item={pieceItem}
                  onClose={() => {}}
                  saleContext={buildInventoryDetailSaleContext({
                    finalPrice: detail.saleLine.finalPrice,
                    saleDate: detail.summary.saleDate,
                  })}
                />
              ) : (
                <Alert severity="warning">
                  No se pudo cargar el detalle completo de la pieza (
                  {detail.summary.productCodeUnique}).
                </Alert>
              )}
            </Box>
          )}

          {activeTab === 'sale' && (
            <Box>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                {SALES_ORDER_NUMBER_LABEL} {detail.summary.orderCode}
                {BRANCH_RECEIPT_NUMBERING_ENABLED && detail.summary.branchReceiptCode
                  ? ` · ${BRANCH_RECEIPT_NUMBER_LABEL} ${detail.summary.branchReceiptCode}`
                  : ''}{' '}
                ·{' '}
                {formatCommercialSaleDate(
                  {
                    saleDate: detail.summary.saleDate,
                    createdAt: detail.summary.saleDate,
                  },
                  'es-ES',
                  { includeTime: true },
                )}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Tipo
                  </Typography>
                  <Typography variant="body2">{detail.summary.categoryName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Vendedor
                  </Typography>
                  <Typography variant="body2">{detail.summary.sellerDisplayName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Sucursal
                  </Typography>
                  <Typography variant="body2">{detail.summary.locationName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Cliente
                  </Typography>
                  <Typography variant="body2">
                    {detail.summary.hasCustomer ? (detail.summary.customerName ?? 'Sí') : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Precio de Venta (Pieza)
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrency(detail.summary.finalPrice)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Factura
                  </Typography>
                  <Typography variant="body2">
                    {getSaleInvoiceLabel(detail.summary.hasInvoice)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Estado de la orden
                  </Typography>
                  <Typography variant="body2">{statusLabel}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Precio y descuento de la pieza
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Precio original
                  </Typography>
                  <Typography variant="body2">
                    {formatCurrency(detail.saleLine.originalPrice)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Precio final
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(detail.saleLine.finalPrice)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Descuento
                  </Typography>
                  <Typography variant="body2">
                    {hasDiscount
                      ? formatDiscountDisplay(
                          detail.saleLine.discountType,
                          Number(detail.saleLine.discountValue ?? 0),
                          detail.saleLine.originalPrice,
                        )
                      : 'Sin descuento'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <S.PaymentsBox>
                <S.PaymentsHeader>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Pagos de la venta
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total: {formatCurrency(detail.order.totalAmount)} · Pagado:{' '}
                    {formatCurrency(detail.order.totalPaid)} · Saldo:{' '}
                    {formatCurrency(detail.order.balanceDue)}
                  </Typography>
                </S.PaymentsHeader>
                {detail.payments.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Sin pagos registrados.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                            Fecha
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                            Monto
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                            Método
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                            Procesado por
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                            Notas
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.payments.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>{formatDate(p.paymentDate)}</TableCell>
                            <TableCell>{formatCurrency(p.paymentAmount)}</TableCell>
                            <TableCell>
                              {p.paymentMethodName ||
                                PAYMENT_METHOD_LABELS[
                                  p.paymentMethod as keyof typeof PAYMENT_METHOD_LABELS
                                ] ||
                                p.paymentMethod ||
                                '—'}
                            </TableCell>
                            <TableCell>{p.processedByName ?? '—'}</TableCell>
                            <TableCell>
                              {getDisplayablePaymentNotes(
                                p.paymentNotes != null ? String(p.paymentNotes) : null,
                              ) ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </S.PaymentsBox>

              <ProductHistorySection
                orderId={detail.order.id}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                embeddedHistory={embeddedHistory}
                sectionTitle="Historial de esta pieza en la venta"
                skipPermissionCheck
              />
            </Box>
          )}
        </Box>
      )}
    </DetailModal>
  );
};

export default SoldPieceDetailModal;
